import React, { useLayoutEffect, useRef, useState } from 'react';
import Reveal from './Reveal';
import { contar, prefiereQuieto } from '../utils/motion';
import { pesos } from '../utils/muestra';

// Demo interactiva con datos de MUESTRA. Reproduce lo que hace la app de verdad:
//  - gasto por categoría (Charts/Dashboard),
//  - avisos de presupuesto al llegar al 80 % y al 100 % (BudgetAlerts.jsx),
//  - gastos hormiga (Pro): pequeños y repetidos, ≈ total × 12 al año (ml/main.py "anualizado").
// Solo transform/opacity en la parte animada; con prefers-reduced-motion los números cambian sin conteo.
const CATS = [
  { id: 'comida', n: 'Comida y súper', icon: '🛒', monto: 3200, pres: 3900 },
  { id: 'transporte', n: 'Transporte', icon: '🚗', monto: 1450, pres: 2000 },
  { id: 'cafe', n: 'Cafeterías', icon: '☕', monto: 780, pres: 600, hormiga: true },
  { id: 'oxxo', n: 'Tiendas de conveniencia', icon: '🏪', monto: 480, pres: 500, hormiga: true },
  { id: 'subs', n: 'Suscripciones', icon: '📺', monto: 620, pres: 800 },
  { id: 'entre', n: 'Entretenimiento', icon: '🎬', monto: 900, pres: 1500 },
];
const TOTAL = CATS.reduce((a, c) => a + c.monto, 0);
const MAX = Math.max(...CATS.map(c => c.monto));
const HORMIGA = CATS.filter(c => c.hormiga).reduce((a, c) => a + c.monto, 0);

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900';

// Número que "rueda" del valor anterior al nuevo (solo cambia el texto; ancho tabular).
function Cifra({ valor, className = '' }) {
  const ref = useRef(null);
  const previo = useRef(valor);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const desde = previo.current;
    previo.current = valor;
    if (desde === valor || prefiereQuieto()) { el.textContent = pesos(valor); return; }
    return contar(el, desde, valor, { duracion: 380, formato: pesos });
  }, [valor]);
  return <span ref={ref} className={`tabular-nums ${className}`}>{pesos(valor)}</span>;
}

function estado(pct) {
  if (pct >= 100) return { txt: 'Te pasaste del presupuesto', cls: 'text-red-700 dark:text-red-300', barra: 'bg-red-600' };
  if (pct >= 80) return { txt: 'Aviso: ya vas en el 80 % o más del presupuesto', cls: 'text-amber-800 dark:text-amber-300', barra: 'bg-amber-500' };
  return { txt: 'Vas bien con este presupuesto', cls: 'text-green-800 dark:text-green-300', barra: 'bg-green-600' };
}

export default function MiniDemoGastos() {
  const [sel, setSel] = useState('cafe');
  const [soloHormiga, setSoloHormiga] = useState(false);
  const cat = CATS.find(c => c.id === sel);
  const pct = Math.round((cat.monto / cat.pres) * 100);
  const est = estado(pct);

  return (
    <section id="demo" className="py-20 px-4 scroll-mt-16">
      <div className="max-w-5xl mx-auto">
        <Reveal><div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black mb-3">Mira en qué se va tu dinero</h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            Toca una categoría para ver cuánto llevas y qué tan cerca estás de tu presupuesto. Activa los gastos hormiga para ver cuánto suman.
          </p>
        </div></Reveal>

        <Reveal delay={80}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 px-5 py-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-white text-xs font-semibold">Ejemplo ilustrativo · datos de muestra</span>
              <span className="text-white text-xs">Gastos del mes: <Cifra valor={TOTAL} className="font-bold" /></span>
            </div>
            <div className="p-4 sm:p-6 grid md:grid-cols-5 gap-6">
              <div className="md:col-span-3">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="text-sm font-semibold" id="md-cats">Categorías del mes</p>
                  <button type="button" role="switch" aria-checked={soloHormiga} onClick={() => setSoloHormiga(v => !v)}
                    className={`inline-flex items-center gap-2 min-h-[44px] text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border lp-press ${FOCUS} ${soloHormiga ? 'bg-amber-100 border-amber-400 text-amber-900 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-100' : 'bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-800 dark:text-gray-100'}`}>
                    <span aria-hidden="true">🐜</span> Solo gastos hormiga
                  </button>
                </div>
                <ul aria-labelledby="md-cats" className="space-y-2">
                  {CATS.map(c => {
                    const activa = c.id === sel;
                    const tenue = soloHormiga && !c.hormiga;
                    return (
                      <li key={c.id}>
                        <button type="button" aria-pressed={activa} onClick={() => setSel(c.id)}
                          className={`md-fila w-full text-left rounded-xl border px-3 py-2.5 ${FOCUS} ${tenue ? 'md-tenue' : ''} ${activa ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}>
                          <span className="flex items-center justify-between gap-3 text-sm">
                            <span className="flex items-center gap-2 min-w-0"><span aria-hidden="true">{c.icon}</span><span className="font-medium truncate">{c.n}</span>{c.hormiga && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">hormiga</span>}</span>
                            <span className="font-semibold tabular-nums shrink-0">{pesos(c.monto)}</span>
                          </span>
                          <span aria-hidden="true" className="mt-2 block h-1.5 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden">
                            <span className={`md-bar block h-full w-full rounded-full ${c.hormiga ? 'bg-amber-500' : 'bg-blue-600'}`} style={{ transform: `scaleX(${c.monto / MAX})` }} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="md:col-span-2" aria-live="polite">
                <div className="rounded-xl bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700 p-5 h-full">
                  {soloHormiga ? (
                    <>
                      <p className="text-sm font-semibold mb-1">🐜 Gastos hormiga</p>
                      <p className="text-3xl font-black"><Cifra valor={HORMIGA} /></p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">este mes</p>
                      <p className="text-sm font-semibold text-red-700 dark:text-red-300">≈ <Cifra valor={HORMIGA * 12} />/año</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-3 leading-relaxed">
                        Son gastos chicos que se repiten. MONIMEX los detecta y te dice cuánto sumarían en un año. <span className="font-semibold">Detección de gastos hormiga: plan Pro.</span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold mb-1"><span aria-hidden="true">{cat.icon}</span> {cat.n}</p>
                      <p className="text-3xl font-black"><Cifra valor={cat.monto} /></p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{Math.round((cat.monto / TOTAL) * 100)} % de tus gastos del mes</p>
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
                        <span>Presupuesto {pesos(cat.pres)}</span><span className="tabular-nums">{pct} %</span>
                      </div>
                      <div aria-hidden="true" className="h-2 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
                        <div className={`md-bar h-full w-full rounded-full ${est.barra}`} style={{ transform: `scaleX(${Math.min(1, pct / 100)})` }} />
                      </div>
                      <p className={`text-sm font-semibold mt-3 ${est.cls}`}>{est.txt}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">En tu panel verás un aviso cuando un presupuesto llegue al 80 % y al 100 %.</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
